/**
 * Settings information architecture (DECISIONS §19, UX-048, UX-081).
 *
 *   Business            — Business profile, Brand kit, Your web address, Email, Document templates, Services & pricing, Import data
 *   Team                — people, roles, invites
 *   Phone & texting     — one phone page with tabs (line, calls, texting, quiet hours), the AI front desk, usage
 *   Money               — sales tax, card fees, how to pay; Payments (the business's Stripe account)
 *   Calendar & booking  — online booking, working hours, calendar feed
 *   Integrations        — connected apps (Google, QuickBooks, email, website form), API keys
 *   Account             — the BizForge plan: billing, seats, invoices (owner)
 *   You                 — profile, notifications, password
 *
 * THE single source of Settings routes and labels. Every section is a route,
 * /settings/<id>; the phone page adds /settings/phone/<tab>. Links anywhere in
 * the app use settingsPath(key) / settingsLabel(key) from here
 * (lib/settingsRoutes re-exports them). Old routes keep working through
 * LEGACY_SETTINGS_REDIRECTS.
 */

import type React from 'react'
import {
  Bell, Briefcase, Receipt, Building2, Calendar as CalendarIcon, CalendarClock, CreditCard, FileText,
  Bot, Gauge, Sparkles, Globe, KeyRound, Link as LinkIcon, Lock, Mail, Palette, Percent, Phone, Tags, Upload, User, Users,
} from 'lucide-react'
import type { CapabilityName } from '@/lib/permissions'
import { SETTINGS_SECTION_CAPABILITY } from '@/components/Navigation/navItems'

export type SettingsGroupId = 'business' | 'team' | 'phone' | 'money' | 'calendar' | 'integrations' | 'account' | 'you'

export const SETTINGS_GROUPS: { id: SettingsGroupId; label: string }[] = [
  { id: 'business',     label: 'Business' },
  { id: 'team',         label: 'Team' },
  { id: 'phone',        label: 'Phone & texting' },
  { id: 'money',        label: 'Money' },
  { id: 'calendar',     label: 'Calendar & booking' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'account',      label: 'Account' },
  { id: 'you',          label: 'You' },
]

export type SettingsSectionId =
  | 'business' | 'brand' | 'domain' | 'email' | 'documents' | 'pricing' | 'import' | 'ask-ai'
  | 'team'
  | 'phone' | 'ai-front-desk' | 'usage'
  | 'money' | 'payments'
  | 'booking' | 'availability' | 'calendar'
  | 'integrations' | 'api'
  | 'billing'
  | 'profile' | 'notifications' | 'security'

export interface SettingsSectionDef {
  id: SettingsSectionId
  group: SettingsGroupId
  label: string
  subtitle: string
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>
  /** Hidden from people without this capability (server enforces it too). */
  capability?: CapabilityName
  /** Extra words people search for. */
  keywords?: string
}

// Icons are neutral: a settings section isn't a phase of work (lib/phaseTone).
const SECTION_LIST: Omit<SettingsSectionDef, 'capability'>[] = [
  { id: 'business', group: 'business', label: 'Business profile', subtitle: 'Name, contact details, tax ID, time zone', icon: Building2, keywords: 'company legal name email website license address timezone time zone google review link tax id ein industry trades ready payments texting a2p' },
  { id: 'brand', group: 'business', label: 'Brand kit', subtitle: 'Logo and colors on quotes and invoices', icon: Palette, keywords: 'logo color colour pdf quote invoice look test' },
  { id: 'domain', group: 'business', label: 'Your web address', subtitle: 'Sign in at your own domain', icon: Globe, keywords: 'domain custom domain hostname url address dns cname txt login white label' },
  { id: 'email', group: 'business', label: 'Email', subtitle: 'How your email is sent, reply copies, your own domain', icon: Mail, keywords: 'email sending from address reply replies forward copy inbox domain dkim spf dmarc dns cname sendgrid deliverability bounce unsubscribe' },
  { id: 'documents', group: 'business', label: 'Document templates', subtitle: 'Your own letterhead, form or Word file', icon: FileText, keywords: 'letterhead template form pdf word docx quote invoice receipt layout stationery' },
  { id: 'pricing', group: 'business', label: 'Services & pricing', subtitle: 'Service types and your price book', icon: Tags, keywords: 'price book catalog items service types trades' },
  { id: 'import', group: 'business', label: 'Import data', subtitle: 'From a spreadsheet, Jobber or Housecall Pro', icon: Upload, keywords: 'import csv spreadsheet excel upload migrate move switch jobber housecall pro clients customers price book quotes estimates jobs invoices undo' },
  { id: 'ask-ai', group: 'business', label: 'Ask AI', subtitle: 'Proactive suggestions, house rules, your own agent', icon: Sparkles, keywords: 'assistant ai ask proactive suggestions nudges follow up automation morning brief house rules agent webhook mcp grokbot chatgpt' },

  // Mount point the Team & roles lane fills.
  { id: 'team', group: 'team', label: 'Team', subtitle: 'People, roles and invites', icon: Users, keywords: 'users invite roles permissions crew office' },

  { id: 'phone', group: 'phone', label: 'Phone & texting', subtitle: 'Your line, who rings, texting registration, quiet hours', icon: Phone, keywords: 'twilio numbers ring groups routing hours voicemail missed call ai receptionist text sms a2p 10dlc registration brand campaign quiet hours' },
  { id: 'ai-front-desk', group: 'phone', label: 'AI front desk', subtitle: 'Answers calls, books estimates, hands off to your team', icon: Bot, keywords: 'ai receptionist front desk voice agent answer calls book estimates greeting hand off transfer call packs auto top-up usage minutes recording transcript' },
  { id: 'usage', group: 'phone', label: 'Usage', subtitle: 'Calls, texts and AI this month', icon: Gauge, keywords: 'billing usage minutes sms cost cap' },

  { id: 'money', group: 'money', label: 'Tax & payments', subtitle: 'Sales tax, card fees, how to pay', icon: Percent, keywords: 'sales tax rate taxable card fee surcharge how to pay payment terms financing acorn' },
  { id: 'payments', group: 'money', label: 'Payments', subtitle: 'Take cards and bank payments, see payouts', icon: CreditCard, keywords: 'stripe connect card ach bank pay now online payments payouts deposit onboarding test mode' },

  { id: 'booking', group: 'calendar', label: 'Online booking', subtitle: 'Your booking link, appointments and reminders', icon: Briefcase, keywords: 'booking page link services appointments reminders confirmation email cancel' },
  { id: 'availability', group: 'calendar', label: 'Your working hours', subtitle: 'When you can be booked, and calendar privacy', icon: CalendarClock, keywords: 'availability hours schedule privacy busy' },
  { id: 'calendar', group: 'calendar', label: 'Calendar feed', subtitle: 'See bookings in Apple, Google or Outlook', icon: CalendarIcon, keywords: 'ical ics subscribe sync' },

  { id: 'integrations', group: 'integrations', label: 'Connected apps', subtitle: 'Google, QuickBooks, email sending, website form', icon: LinkIcon, keywords: 'google gmail calendar quickbooks qbo intuit accounting bookkeeping sync sendgrid email website lead form workspace id' },
  { id: 'api', group: 'integrations', label: 'API keys', subtitle: 'Keys for AI assistants and other software', icon: KeyRound, keywords: 'api key mcp agent token' },

  { id: 'billing', group: 'account', label: 'Billing', subtitle: 'Your plan, seats, card and invoices', icon: Receipt, keywords: 'plan subscription billing invoice card payment method seats upgrade downgrade cancel trial annual monthly add-on' },

  { id: 'profile', group: 'you', label: 'Profile', subtitle: 'Your name and mobile number', icon: User, keywords: 'name phone mobile me' },
  { id: 'notifications', group: 'you', label: 'Notifications', subtitle: 'What you get told about, and how', icon: Bell, keywords: 'alerts email text push app' },
  { id: 'security', group: 'you', label: 'Password', subtitle: 'Change your password, sign out other devices', icon: Lock, keywords: 'password security sign in sign out devices' },
]

/**
 * Gating comes from one table shared with the nav and the contract test
 * (SETTINGS_SECTION_CAPABILITY, F1 dotted keys) so it can't drift.
 */
export const SETTINGS_SECTIONS: SettingsSectionDef[] = SECTION_LIST.map((s) => ({
  ...s,
  capability: SETTINGS_SECTION_CAPABILITY[s.id],
}))

const SECTION_IDS = new Set<string>(SETTINGS_SECTIONS.map((s) => s.id))

// ─── Phone tabs (/settings/phone/<tab>) ───────────────────────────────────────

/** Tabs inside the phone page; components/Settings/phone/PhoneSettings renders them. */
export const PHONE_TABS = [
  { id: 'line', label: 'Line & numbers' },
  { id: 'calls', label: 'Calls & ring groups' },
  { id: 'texting', label: 'Texting & registration' },
  { id: 'quiet-hours', label: 'Quiet hours' },
] as const

export type PhoneTabId = (typeof PHONE_TABS)[number]['id']

// ─── Link map ─────────────────────────────────────────────────────────────────

const sectionLabel = (id: SettingsSectionId) => SETTINGS_SECTIONS.find((s) => s.id === id)!.label
const phoneTabLabel = (id: PhoneTabId) => PHONE_TABS.find((t) => t.id === id)!.label

/**
 * Every in-app link into Settings (UX-088). `path` is the route; `label` is how
 * copy names the place ("Settings → Phone & texting").
 */
export const SETTINGS_ROUTES = {
  ...(Object.fromEntries(
    SECTION_LIST.map((s) => [s.id, { path: `/settings/${s.id}`, label: `Settings → ${s.label}` }]),
  ) as Record<SettingsSectionId, { path: string; label: string }>),
  phoneLine: { path: '/settings/phone/line', label: `Settings → Phone & texting → ${phoneTabLabel('line')}` },
  phoneCalls: { path: '/settings/phone/calls', label: `Settings → Phone & texting → ${phoneTabLabel('calls')}` },
  texting: { path: '/settings/phone/texting', label: `Settings → Phone & texting → ${phoneTabLabel('texting')}` },
  quietHours: { path: '/settings/phone/quiet-hours', label: `Settings → Phone & texting → ${phoneTabLabel('quiet-hours')}` },
  /** Billing tools live outside Settings; they're opened from Tax & payments. */
  billingTools: { path: '/payments/tools', label: `Billing tools (Settings → ${sectionLabel('money')})` },
} as const

export type SettingsRouteKey = keyof typeof SETTINGS_ROUTES

/** The route for a section or settings page. */
export function settingsPath(key: SettingsRouteKey): string {
  return SETTINGS_ROUTES[key].path
}

/** How copy names a settings page: "Settings → Usage". */
export function settingsLabel(key: SettingsRouteKey): string {
  return SETTINGS_ROUTES[key].label
}

// ─── Old routes ───────────────────────────────────────────────────────────────

/** Old /settings/<segment> routes (bookmarks, emails, notices) → their new home. */
export const LEGACY_SETTINGS_REDIRECTS: Record<string, string> = {
  services: settingsPath('booking'),
  twilio: settingsPath('phoneLine'),
  a2p: settingsPath('texting'),
  texting: settingsPath('texting'),
  telephony: settingsPath('phone'),
  workspace: settingsPath('business'),
  tax: settingsPath('money'),
  stripe: settingsPath('payments'),
  quickbooks: settingsPath('integrations'),
}

/** A URL segment → the section to show, or null when it isn't a current section. */
export function resolveSectionId(segment: string): SettingsSectionId | null {
  return SECTION_IDS.has(segment) ? (segment as SettingsSectionId) : null
}

/** Where an old /settings/<segment> route now lives, or null. */
export function legacySettingsRedirect(segment: string): string | null {
  return LEGACY_SETTINGS_REDIRECTS[segment] ?? null
}

/**
 * Does `to` (a path, optionally with ?query/#hash) open a real Settings page?
 * Old routes count only through their redirect target. Used by tests to keep
 * every link in the app honest.
 */
export function isSettingsRoute(to: string): boolean {
  const path = to.split(/[?#]/)[0].replace(/\/+$/, '')
  if (path === '/settings') return true
  if (path === settingsPath('billingTools')) return true
  const m = /^\/settings\/([^/]+)(?:\/([^/]+))?$/.exec(path)
  if (!m || !resolveSectionId(m[1])) return false
  if (!m[2]) return true
  return m[1] === 'phone' && PHONE_TABS.some((t) => t.id === m[2])
}

/** First section a person can open (desktop opens it at /settings). */
export function defaultSectionFor(visible: SettingsSectionDef[]): SettingsSectionId {
  return visible[0]?.id ?? 'profile'
}

export function searchSections(sections: SettingsSectionDef[], query: string): SettingsSectionDef[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (words.length === 0) return sections
  return sections.filter((s) => {
    const group = SETTINGS_GROUPS.find((g) => g.id === s.group)?.label ?? ''
    const hay = `${s.label} ${s.subtitle} ${group} ${s.keywords ?? ''}`.toLowerCase()
    return words.every((w) => hay.includes(w))
  })
}
